import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import logo from "../images/techvanto-logo.png";
import { NAV_LINKS } from "../data/navLinks";
import { scrollToContact } from "./scrollToContact";

const LOGO_SRC = logo;

function NavLink({ to, children, style, onClick, className }) {
  const isHash = to && to.includes("#");
  const isExternal = to && to.startsWith("http");

  if (!to || to === "#") {
    return (
      <span style={style} className={className} onClick={onClick}>
        {children}
      </span>
    );
  }

  if (isExternal) {
    return (
      <a href={to} style={style} className={className} onClick={onClick}>
        {children}
      </a>
    );
  }

  if (isHash) {
    const [path, hash] = to.split("#");
    const targetPath = path || "/";
    return (
      <Link
        to={`${targetPath}#${hash}`}
        style={style}
        className={className}
        onClick={(e) => {
          onClick?.(e);
          if (window.location.pathname === targetPath || (targetPath === "/" && window.location.pathname === "/")) {
            e.preventDefault();
            const el = document.getElementById(hash);
            if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
          }
        }}
      >
        {children}
      </Link>
    );
  }

  return (
    <Link to={to} style={style} className={className} onClick={onClick}>
      {children}
    </Link>
  );
}

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [mobileDropdown, setMobileDropdown] = useState(null);

  const currentPath = typeof window !== 'undefined' ? window.location.pathname : '/';

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Close mobile menu on route change / outside click
  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => { document.body.style.overflow = ""; };
  }, [mobileOpen]);

  return (
    <nav style={{
      position: "fixed", top: 0, left: 0, right: 0, zIndex: 1000,
      background: scrolled ? "rgba(255,255,255,0.97)" : "rgba(255,255,255,0.9)",
      backdropFilter: "blur(16px)",
      borderBottom: scrolled ? "1px solid #E5E7EB" : "1px solid transparent",
      boxShadow: scrolled ? "0 2px 24px rgba(0,0,0,0.08)" : "none",
      transition: "all .3s ease",
    }}>
      {/* Top bar */}
      <div style={{
        background: "linear-gradient(90deg,#1E3A8A,#3B82F6)",
        color: "#fff", fontSize: 12, padding: "6px 40px",
        display: "flex", justifyContent: "space-between", alignItems: "center",
        flexWrap: "wrap", gap: 8,
      }}>
        <div style={{ display: "flex", gap: 20, flexWrap: "wrap" }}>
          <span>📞 +91 96462 06032</span>
          <span>✉️ info@techvanto.com</span>
        </div>
        <span className="topbar-right" style={{ opacity: .85 }}>🌏 Trusted by 1500+ clients across 20+ countries</span>
      </div>

      {/* Main nav bar */}
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        padding: "0 40px", height: 68, maxWidth: 1280, margin: "0 auto",
      }}>
        <a href="/" style={{ display: "flex", alignItems: "center", flexShrink: 0, textDecoration: "none" }}>
          <img src={LOGO_SRC} alt="Techvanto" style={{ height: 150, width: "auto", objectFit: "contain", display: "block" }} />
        </a>

        {/* Desktop links */}
        <div className="nav-links" style={{ alignItems: "center", gap: 4 }}>
          {NAV_LINKS.map(link => {
            const isActive = link.link && currentPath.includes(link.label.toLowerCase());
            return (
              <div key={link.label} style={{ position: "relative" }}
                onMouseEnter={() => link.dropdown && setActiveDropdown(link.label)}
                onMouseLeave={() => setActiveDropdown(null)}
              >
                {link.link ? (
                  <a href={link.link} style={{
                    background: isActive ? "#EFF6FF" : "none",
                    border: isActive ? "1px solid #BFDBFE" : "1px solid transparent",
                    cursor: "pointer", padding: "8px 14px", fontSize: 14, fontWeight: 600,
                    color: isActive ? "#3B82F6" : "#374151",
                    display: "flex", alignItems: "center", gap: 4, transition: "color .2s, background .2s",
                    fontFamily: "'Sora',sans-serif", borderRadius: 8, textDecoration: "none"
                  }}>
                    {link.label}
                  </a>
                ) : (
                  <button style={{
                    background: (link.label === "Services" && currentPath === "/web-development") ? "#EFF6FF" : "none",
                    border: (link.label === "Services" && currentPath === "/web-development") ? "1px solid #BFDBFE" : "1px solid transparent",
                    cursor: "pointer", padding: "8px 14px", fontSize: 14, fontWeight: 600,
                    color: (link.label === "Services" && currentPath === "/web-development") ? "#3B82F6" : activeDropdown === link.label ? "#3B82F6" : "#374151",
                    display: "flex", alignItems: "center", gap: 4, transition: "color .2s",
                    fontFamily: "'Sora',sans-serif", borderRadius: 8,
                  }}>
                    {link.label}
                    {link.dropdown && <span style={{ fontSize: 10, transition: "transform .2s", transform: activeDropdown === link.label ? "rotate(180deg)" : "rotate(0)" }}>▾</span>}
                  </button>
                )}
                {link.dropdown && activeDropdown === link.label && (
                  <div style={{
                    position: "absolute", top: "100%", left: 0, zIndex: 200,
                    background: "#fff", border: "1px solid #E5E7EB", borderRadius: 12,
                    boxShadow: "0 16px 48px rgba(0,0,0,0.12)", minWidth: 220,
                    padding: "8px 0", animation: "fadeIn .2s ease",
                  }}>
                    {link.dropdown.map(item => (
                      <a key={item.name} href={item.link} style={{
                        display: "block", padding: "9px 20px", fontSize: 13.5,
                        color: currentPath === item.link ? "#3B82F6" : "#374151",
                        background: currentPath === item.link ? "#EFF6FF" : "none",
                        fontWeight: currentPath === item.link ? 700 : 500,
                        textDecoration: "none", transition: "all .15s",
                      }}
                        onMouseEnter={e => { e.target.style.background = "#EFF6FF"; e.target.style.color = "#3B82F6"; }}
                        onMouseLeave={e => { 
                          e.target.style.background = currentPath === item.link ? "#EFF6FF" : "none"; 
                          e.target.style.color = currentPath === item.link ? "#3B82F6" : "#374151"; 
                        }}
                      >{item.name}</a>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
          <a href="#contact" className="nav-cta" style={{
            background: "linear-gradient(135deg,#1E3A8A,#3B82F6)", color: "#fff",
            padding: "10px 22px", borderRadius: 8, fontSize: 13.5, fontWeight: 700,
            textDecoration: "none", boxShadow: "0 4px 14px rgba(59,130,246,.35)",
            transition: "transform .2s, box-shadow .2s", fontFamily: "'Sora',sans-serif",
          }}
            onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-1px)"; e.currentTarget.style.boxShadow = "0 8px 20px rgba(59,130,246,.45)"; }}
            onMouseLeave={e => { e.currentTarget.style.transform = "none"; e.currentTarget.style.boxShadow = "0 4px 14px rgba(59,130,246,.35)"; }}
          >Get Free Consultation</a>
          <button onClick={() => setMobileOpen(!mobileOpen)} className="mob-menu-btn" style={{
            background: mobileOpen ? "#EFF6FF" : "none",
            border: "1px solid #E5E7EB", borderRadius: 8,
            padding: "8px 12px", cursor: "pointer", fontSize: 18, color: "#374151",
            transition: "all .2s",
          }}>
            {mobileOpen ? "✕" : "☰"}
          </button>
        </div>
      </div>

      {/* ── Mobile Menu (Full-featured with dropdowns) ── */}
      {mobileOpen && (
        <div style={{
          background: "#fff", borderTop: "1px solid #E5E7EB",
          maxHeight: "calc(100vh - 120px)", overflowY: "auto",
          boxShadow: "0 16px 48px rgba(0,0,0,0.12)",
        }}>
         

          {/* Nav links with nested dropdowns */}
          <div style={{ padding: "8px 0" }}>
            {NAV_LINKS.map(link => (
              <div key={link.label}>
                {link.dropdown ? (
                  <>
                    {/* Dropdown parent — toggle on click */}
                    <button
                      onClick={() => setMobileDropdown(mobileDropdown === link.label ? null : link.label)}
                      style={{
                        width: "100%", textAlign: "left",
                        padding: "13px 24px",
                        background: mobileDropdown === link.label ? "#EFF6FF" : "none",
                        border: "none", borderBottom: "1px solid #F3F4F6",
                        color: mobileDropdown === link.label ? "#3B82F6" : "#374151",
                        fontWeight: 600, fontSize: 15,
                        fontFamily: "'Sora',sans-serif",
                        cursor: "pointer",
                        display: "flex", alignItems: "center", justifyContent: "space-between",
                      }}
                    >
                      {link.label}
                      <span style={{
                        fontSize: 11, transition: "transform .2s",
                        transform: mobileDropdown === link.label ? "rotate(180deg)" : "rotate(0)",
                        color: "#9CA3AF",
                      }}>▾</span>
                    </button>
                    {/* Dropdown items */}
                    {mobileDropdown === link.label && (
                      <div style={{ background: "#F8FAFC", borderBottom: "1px solid #F3F4F6" }}>
                        {link.dropdown.map(item => (
                          <a
                            key={item.name}
                            href={item.link}
                            onClick={() => setMobileOpen(false)}
                            style={{
                              display: "block",
                              padding: "11px 24px 11px 40px",
                              fontSize: 14,
                              color: currentPath === item.link ? "#3B82F6" : "#4B5563",
                              background: currentPath === item.link ? "#EFF6FF" : "none",
                              fontWeight: currentPath === item.link ? 700 : 500,
                              textDecoration: "none",
                              borderBottom: "1px solid #F1F5F9",
                              fontFamily: "'Sora',sans-serif",
                            }}
                          >
                            → {item.name}
                          </a>
                        ))}
                      </div>
                    )}
                  </>
                ) : (
                  <a
                    href={link.link || "#"}
                    onClick={() => setMobileOpen(false)}
                    style={{
                      display: "flex", alignItems: "center", justifyContent: "space-between",
                      padding: "13px 24px",
                      color: currentPath === link.link ? "#3B82F6" : "#374151",
                      background: currentPath === link.link ? "#EFF6FF" : "none",
                      fontWeight: 600, fontSize: 15,
                      textDecoration: "none",
                      borderBottom: "1px solid #F3F4F6",
                      fontFamily: "'Sora',sans-serif",
                    }}
                  >
                    {link.label}
                    <span style={{ fontSize: 11, color: "#9CA3AF" }}>›</span>
                  </a>
                )}
              </div>
            ))}
          </div>

          {/* CTA buttons at bottom of mobile menu */}
          <div style={{ padding: "16px 24px 24px", display: "flex", flexDirection: "column", gap: 10 }}>
            <a
              href="#contact"
              onClick={() => setMobileOpen(false)}
              style={{
                background: "linear-gradient(135deg,#1E3A8A,#3B82F6)", color: "#fff",
                padding: "14px", borderRadius: 10, textAlign: "center",
                fontWeight: 700, fontSize: 15, textDecoration: "none",
                fontFamily: "'Sora',sans-serif",
                boxShadow: "0 6px 20px rgba(59,130,246,.35)",
              }}
            >
              🚀 Get Free Consultation
            </a>
            <a
              href="tel:+919646206032"
              style={{
                background: "#F1F5F9", color: "#1E3A8A",
                padding: "14px", borderRadius: 10, textAlign: "center",
                fontWeight: 700, fontSize: 15, textDecoration: "none",
                fontFamily: "'Sora',sans-serif", border: "1px solid #BFDBFE",
              }}
            >
              📞 Call Us: +91 96462 06032
            </a>
          </div>
        </div>
      )}
    </nav>
  );
}
